import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function About() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'KAIYUN导航',
    url: 'https://kaiyun-navi.com',
    description:
      'KAIYUN导航致力于为用户提供 kaiyun.con 官方入口的完整导航指引、平台介绍及常见问题解答服务。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@kaiyun-navi.com',
      availableLanguage: ['Chinese'],
    },
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - KAIYUN导航',
    description: '了解KAIYUN导航的品牌故事、平台信誉及联系方式。',
    url: 'https://kaiyun-navi.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: 'KAIYUN导航',
      url: 'https://kaiyun-navi.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }}
      />

      <SEOHead
        title="关于我们 | kaiyun.con导航平台 - KAIYUN导航"
        description="了解KAIYUN导航的品牌故事、平台信誉及联系方式。我们致力于为用户提供 kaiyun.con 官方入口的完整导航指引与常见问题解答服务。"
        keywords="kaiyun.con导航,关于我们,品牌介绍,联系方式,平台信誉"
        canonicalUrl="https://kaiyun-navi.com/about"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Header */}
          <section className="bg-background-50 border-b border-background-200">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-4 font-[var(--font-heading)]">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-foreground-600 mt-2">
                了解KAIYUN导航的品牌故事、平台信誉及联系方式
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div
                className={`transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 font-[var(--font-heading)]">
                  品牌介绍
                </h2>
                <div className="space-y-4">
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    <strong>KAIYUN导航</strong> 是一个专注于为用户提供 <strong>kaiyun.con</strong> 官方入口导航指引的第三方信息服务网站。我们致力于帮助用户快速、准确地找到
                    <strong>www.kaiyun.con</strong> 的官方访问入口，并提供详尽的平台功能介绍、使用教程和常见问题解答。
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    自网站成立以来，我们始终坚持以用户需求为导向，不断更新和完善导航信息内容。无论是初次接触
                    <strong>kaiyun.con</strong> 的新用户，还是希望了解更多平台功能的老用户，都可以在本站找到有价值的信息。
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    我们的团队持续关注 <strong>kaiyun.con</strong> 平台的最新动态，确保提供的导航信息和访问指引始终准确、及时。同时，我们也积极收集和回应用户的反馈，不断优化网站内容和用户体验。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 font-[var(--font-heading)]">
                平台信誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="p-5 bg-background-50 rounded-lg border border-background-200 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-lg">
                    <i className="ri-shield-check-line text-xl text-accent-500"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">信息准确</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    我们严格核实每一条导航信息，确保为用户提供准确可靠的 kaiyun.con 官方入口指引。
                  </p>
                </div>
                <div className="p-5 bg-background-50 rounded-lg border border-background-200 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-lg">
                    <i className="ri-time-line text-xl text-accent-500"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">更新及时</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    团队持续跟踪平台动态，第一时间更新导航信息，保证内容时效性。
                  </p>
                </div>
                <div className="p-5 bg-background-50 rounded-lg border border-background-200 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-lg">
                    <i className="ri-user-heart-line text-xl text-accent-500"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">用户至上</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    以用户需求为核心，不断优化网站内容和功能，提供优质的导航服务体验。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Mission */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 font-[var(--font-heading)]">
                我们的使命
              </h2>
              <div className="p-6 bg-background-100 rounded-lg border border-background-200">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  KAIYUN导航的使命是成为用户在搜索 <strong>www.kaiyun.con</strong> 相关关键词时的首选信息平台。我们希望通过清晰、全面、及时的导航指引，帮助每一位用户节省搜索时间，快速获取所需信息。我们将持续提升内容质量，优化用户体验，为
                  <strong>kaiyun.con</strong> 的用户群体提供价值。
                </p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 font-[var(--font-heading)]">
                联系方式
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="p-5 bg-background-50 rounded-lg border border-background-200 flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">support@kaiyun-navi.com</p>
                  </div>
                </div>
                <div className="p-5 bg-background-50 rounded-lg border border-background-200 flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-global-line text-lg text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">官方网站</h3>
                    <p className="text-sm text-foreground-600">https://kaiyun-navi.com</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-5 bg-accent-50 rounded-lg border border-accent-200">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-accent-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-accent-800 mb-1">重要声明</h3>
                    <p className="text-xs md:text-sm text-accent-700 leading-relaxed">
                      本站（KAIYUN导航）为第三方导航指引网站，<strong>并非 kaiyun.con 官方网站</strong>。我们仅提供平台相关的导航信息和使用指引，不涉及任何平台运营业务。如需联系 kaiyun.con 官方平台，请通过其官方渠道进行。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3 font-[var(--font-heading)]">
                需要导航帮助？
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mb-6">
                前往分类导航页面，获取完整的 kaiyun.con 访问指南与使用教程
              </p>
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-lg text-base font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap no-underline"
              >
                进入分类导航
                <i className="ri-compass-3-line"></i>
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}